import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services';
import { validatePasswordStrength } from '../utils/passwordUtils';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Lock, Eye, EyeOff, Loader2, AlertCircle, Check, X } from 'lucide-react';
import GroupSvg from '../svg/Group.svg';

// Helper function to check if user is a hospital user
const isHospitalUserRole = (role) => {
  return role === 'HOSPITAL_USER' || role === 'hospital' || role === 'Hospital';
};

// Helper function to validate current password field
const validateCurrentPassword = (currentPassword) => {
  if (!currentPassword) {
    return 'Current password is required';
  }
  return null;
};

// Helper function to validate new password field
const validateNewPassword = (newPassword, passwordStrength, currentPassword) => {
  if (!newPassword) {
    return 'New password is required';
  }
  if (newPassword.length < 8) {
    return 'Password must be at least 8 characters long';
  }
  if (!passwordStrength.isValid) {
    return 'Password does not meet strength requirements';
  }
  if (currentPassword === newPassword) {
    return 'New password must be different from current password';
  }
  return null;
};

// Helper function to validate confirm password field
const validateConfirmPassword = (confirmPassword, newPassword) => {
  if (!confirmPassword) {
    return 'Please confirm your new password';
  }
  if (newPassword !== confirmPassword) {
    return 'Passwords do not match';
  }
  return null;
};

// Helper function to extract validation errors from error.errors array
const extractValidationErrorsFromArray = (errors) => {
  const validationErrors = {};
  errors.forEach(err => {
    if (err.field === 'currentPassword' || err.field === 'newPassword') {
      validationErrors[err.field] = err.message;
    }
  });
  return validationErrors;
};

// Helper function to check if error message is about current password
const isCurrentPasswordError = (message) => {
  const lowerMessage = message.toLowerCase();
  return lowerMessage.includes('current password') || lowerMessage.includes('incorrect password');
};

// Helper function to handle error.errors array structure
const handleErrorsArray = (errors, setFieldErrors, setSaveError) => {
  const validationErrors = extractValidationErrorsFromArray(errors);
  if (Object.keys(validationErrors).length > 0) {
    setFieldErrors(validationErrors);
  } else {
    setSaveError(errors.map(err => err.message).join(', '));
  }
};

// Helper function to handle error.response.data.errors array structure
const handleResponseErrorsArray = (errors, setFieldErrors, setSaveError) => {
  const validationErrors = extractValidationErrorsFromArray(errors);
  if (Object.keys(validationErrors).length > 0) {
    setFieldErrors(validationErrors);
  } else {
    setSaveError(errors.map(err => err.message).join(', '));
  }
};

// Helper function to handle error.response.data.message
const handleResponseMessage = (message, setFieldErrors, setSaveError) => {
  if (isCurrentPasswordError(message)) {
    setFieldErrors({ currentPassword: message });
  } else {
    setSaveError(message);
  }
};

// Helper function to process error and set appropriate state
const processError = (error, setFieldErrors, setSaveError) => {
  if (error.errors && Array.isArray(error.errors)) {
    handleErrorsArray(error.errors, setFieldErrors, setSaveError);
    return;
  }
  
  if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    handleResponseErrorsArray(error.response.data.errors, setFieldErrors, setSaveError);
    return;
  }
  
  if (error.response?.data?.message) {
    handleResponseMessage(error.response.data.message, setFieldErrors, setSaveError);
    return;
  }
  
  if (error.message) {
    setSaveError(error.message);
    return;
  }
  
  setSaveError('Failed to change password. Please try again.');
};

// Helper function to get password strength color class
const getPasswordStrengthColor = (score) => {
  if (score <= 2) return 'bg-red-500';
  if (score <= 4) return 'bg-yellow-500';
  return 'bg-green-500';
};

// Helper function to get password strength label
const getPasswordStrengthLabel = (score) => {
  if (score <= 2) return 'Weak';
  if (score <= 4) return 'Medium';
  return 'Strong';
};

// Helper function to get confirm password input className
const getConfirmPasswordClassName = (fieldErrors, confirmPassword, newPassword) => {
  const baseClass = 'pl-12 sm:pl-14 pr-12 sm:pr-14';
  if (fieldErrors.confirmPassword) {
    return `${baseClass} border-red-300 focus:ring-red-500`;
  }
  if (confirmPassword && newPassword === confirmPassword) {
    return `${baseClass} border-green-300 focus:ring-green-500`;
  }
  return baseClass;
};

const ChangePassword = ({ onBackToProfile }) => {
  const { user: authUser } = useAuth();
  
  const isHospitalUser = isHospitalUserRole(authUser?.role);
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [], isValid: false });
  const [fieldErrors, setFieldErrors] = useState({});
  // Email-OTP step (everyone except hospital users, who use their own one-step endpoint)
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resendNotice, setResendNotice] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (saveError) setSaveError(null);
    if (saveSuccess) setSaveSuccess(false);
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Validate password strength in real-time for new password
    if (name === 'newPassword') {
      const strength = validatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordRequirementClassName = (isMet) => {
    return isMet ? 'bg-green-500' : 'bg-gray-300';
  };

  const doPasswordsMatch = (password, confirmPassword) => {
    return password === confirmPassword && password !== '';
  };

  const validateForm = () => {
    const errors = {};
    
    const currentPasswordError = validateCurrentPassword(formData.currentPassword);
    if (currentPasswordError) {
      errors.currentPassword = currentPasswordError;
    }
    
    const newPasswordError = validateNewPassword(
      formData.newPassword, 
      passwordStrength, 
      formData.currentPassword
    );
    if (newPasswordError) {
      errors.newPassword = newPasswordError;
    }
    
    const confirmPasswordError = validateConfirmPassword(
      formData.confirmPassword, 
      formData.newPassword
    );
    if (confirmPasswordError) {
      errors.confirmPassword = confirmPasswordError;
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordStrength({ score: 0, feedback: [], isValid: false });
    setOtpStep(false);
    setOtp('');
  };

  const handleSuccess = () => {
    setSaveSuccess(true);
    resetForm();
    
    // Auto-close after 2 seconds
    setTimeout(() => {
      if (onBackToProfile) {
        onBackToProfile();
      }
    }, 2000);
  };

  const callChangePasswordAPI = async () => {
    const passwordData = {
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword
    };

    if (isHospitalUser) {
      return await authService.hospitalChangePassword(passwordData);
    }
    return await authService.changePassword({ ...passwordData, otp });
  };

  // Step 1: check the passwords and email a code, then show the code entry step
  const requestOtp = async () => {
    const result = await authService.requestChangePasswordOtp({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword
    });
    if (!result.success) {
      throw new Error(result.message || 'Failed to send verification code');
    }
    setMaskedEmail(result.data?.email || '');
    setOtp('');
    setOtpStep(true);
  };

  const handleAPIResult = (result) => {
    if (!result.success) {
      throw new Error(result.message || 'Failed to change password');
    }
    handleSuccess();
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (otpStep) {
      if (!/^\d{6}$/.test(otp)) {
        setFieldErrors({ otp: 'Enter the 6-digit code sent to your email' });
        return;
      }
    } else if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      if (!isHospitalUser && !otpStep) {
        await requestOtp();
      } else {
        const result = await callChangePasswordAPI();
        handleAPIResult(result);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      processError(error, setFieldErrors, setSaveError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResendOtp = async () => {
    setIsSaving(true);
    setSaveError(null);
    setResendNotice(false);
    try {
      await requestOtp();
      setResendNotice(true);
    } catch (error) {
      processError(error, setFieldErrors, setSaveError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToPasswords = () => {
    setOtpStep(false);
    setOtp('');
    setSaveError(null);
    setResendNotice(false);
    setFieldErrors({});
  };

  const handleClose = () => {
    if (onBackToProfile) {
      onBackToProfile();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-300" style={{
      background: 'linear-gradient(135deg, rgba(248, 249, 250, 0.6) 0%, rgba(227, 242, 253, 0.6) 100%)'
    }}>
      <div className="w-full max-w-md mx-auto">
        <Card className="shadow-xl rounded-2xl bg-white/80 backdrop-blur-sm relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
            disabled={isSaving}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          
          <CardHeader className="space-y-2 text-center pt-6 pb-6">
            <div className="flex justify-center mb-4">
              <img src={GroupSvg} alt="Smart Ambulance Logo" className="h-10 w-auto" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Change Password
            </CardTitle>
            {/* <CardDescription className="text-gray-600 mt-2">
              Enter your current password and choose a new secure password
            </CardDescription> */}
          </CardHeader>

          <CardContent className="space-y-4 px-4 sm:px-6 pb-4">
            {saveSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  <p className="text-sm text-green-600">Password changed successfully! Redirecting...</p>
                </div>
              </div>
            )}
            
            {saveError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <p className="text-sm text-red-600">{saveError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              {otpStep && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    We sent a 6-digit verification code to <span className="font-semibold text-gray-900">{maskedEmail || 'your email'}</span>.
                    Enter it below to confirm the password change.
                  </p>
                  <div className="space-y-2">
                    <label htmlFor="otp" className="block text-sm font-semibold text-gray-900">
                      Email verification code
                    </label>
                    <Input
                      id="otp"
                      name="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                        if (saveError) setSaveError(null);
                        if (fieldErrors.otp) setFieldErrors(prev => ({ ...prev, otp: '' }));
                      }}
                      className={`text-center tracking-[0.5em] text-lg ${fieldErrors.otp ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="------"
                      disabled={isSaving}
                      autoFocus
                    />
                    {fieldErrors.otp && (
                      <p className="text-sm text-red-600 flex items-center space-x-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{fieldErrors.otp}</span>
                      </p>
                    )}
                    {resendNotice && (
                      <p className="text-sm text-green-600 flex items-center space-x-1">
                        <Check className="w-4 h-4" />
                        <span>A new code has been sent</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={handleBackToPasswords}
                      className="text-gray-600 hover:text-gray-900 underline"
                      disabled={isSaving}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="text-[#1e3a8a] hover:underline font-medium"
                      disabled={isSaving}
                    >
                      Resend code
                    </button>
                  </div>
                </div>
              )}

              {!otpStep && (
                <>
              {/* Current Password */}
              <div className="space-y-2">
                <label htmlFor="currentPassword" className="block text-sm font-semibold text-gray-900">
                  Current Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    className={`pl-12 sm:pl-14 pr-12 sm:pr-14 ${
                      fieldErrors.currentPassword 
                        ? 'border-red-300 focus:ring-red-500' 
                        : ''
                    }`}
                    placeholder="Enter your current password"
                    disabled={isSaving}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
                    disabled={isSaving}
                    aria-label={showPasswords.current ? "Hide password" : "Show password"}
                  >
                    {showPasswords.current ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {fieldErrors.currentPassword && (
                  <p className="text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{fieldErrors.currentPassword}</span>
                  </p>
                )}
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-900">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className={`pl-12 sm:pl-14 pr-12 sm:pr-14 ${
                      fieldErrors.newPassword 
                        ? 'border-red-300 focus:ring-red-500' 
                        : ''
                    }`}
                    placeholder="Enter new password (min. 8 characters)"
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
                    disabled={isSaving}
                    aria-label={showPasswords.new ? "Hide password" : "Show password"}
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div className="mt-2 space-y-2">
                    {/* Strength Bar */}
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength.score)}`}
                          style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-600">
                        {getPasswordStrengthLabel(passwordStrength.score)}
                      </span>
                    </div>
                  </div>
                )}
                
                {fieldErrors.newPassword && (
                  <p className="text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{fieldErrors.newPassword}</span>
                  </p>
                )}
              </div>

              {/* Confirm New Password */}
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-900">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={getConfirmPasswordClassName(
                      fieldErrors, 
                      formData.confirmPassword, 
                      formData.newPassword
                    )}
                    placeholder="Re-enter your new password"
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
                    disabled={isSaving}
                    aria-label={showPasswords.confirm ? "Hide password" : "Show password"}
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{fieldErrors.confirmPassword}</span>
                  </p>
                )}
                {formData.confirmPassword && 
                 formData.newPassword === formData.confirmPassword && 
                 !fieldErrors.confirmPassword && (
                  <p className="text-sm text-green-600 flex items-center space-x-1">
                    <Check className="w-4 h-4" />
                    <span>Passwords match</span>
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              {formData.newPassword && passwordStrength.feedback.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs font-semibold text-gray-800 mb-2">Password Requirements:</p>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {passwordStrength.feedback.map((feedback, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${getPasswordRequirementClassName(false)}`}></div>
                        <span>{feedback}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white font-medium py-3 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:opacity-50"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span>{!isHospitalUser && !otpStep ? 'Send Verification Code' : 'Change Password'}</span>
                      <Check className="w-4 h-4" />
                    </div>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
